import { type FC, useEffect, useMemo, useState } from 'react';
import type { Order, OrderItem, Address, WalletTransaction, CollectionPoint, CakeOrder, Payment, PaymentStatusObject } from '../../types';
import { useToast } from '../../hooks/useToast';
import { useStores } from '../../hooks/useStores';
import { useOrders } from '../../hooks/useOrders';
import CancelOrderModal from './CancelOrderModal';
import StatusBadge from '../StatusBadge';
import {
    MapPinIcon, UserIcon, PhoneIcon, BuildingStorefrontIcon,
    CalendarIcon, ClockIcon, ShoppingBagIcon, TruckIcon, CakeIcon, CheckCircleIcon,
    BanknotesIcon, ArrowUturnLeftIcon, ExclamationTriangleIcon, UsersIcon,
} from '@heroicons/react/24/outline';
import OrderTypeBadge from '../OrderTypeBadge.tsx';

interface OrderOverviewTabProps {
    currentOrder: Order;
    onOrderUpdate: () => Promise<void>;
    orderItems?: OrderItem[];
    customerAddresses?: Address[] | null;
    walletTransactions: WalletTransaction[];
    collectionPoint?: CollectionPoint | null;
    cakeOrder?: CakeOrder | null;
    payments?: Payment[];
}

// Describes a picking shortfall for an item — fewer units scanned at picking
// than the order required. Returns null when fully picked, not yet picked,
// has no picking record, or is a deli/variable-weight line (billed via
// actual_price_cents directly, so a picking shortfall doesn't apply the same way).
function buildPickingShortfall(item: OrderItem): { label: string; tone: 'red' | 'amber' } | null {
    if (item.actual_price_cents != null) return null;
    if (item.picking_status !== 'PARTIAL' && item.picking_status !== 'MISSING') return null;

    const required = item.picking_quantity_required ?? item.quantity;
    const picked   = item.picking_quantity_picked ?? 0;

    return {
        label: item.picking_status === 'MISSING' ? 'Not picked — out of stock' : `${picked} of ${required} picked`,
        tone:  item.picking_status === 'MISSING' ? 'red' : 'amber',
    };
}

// Resolves the canonical status name off a payment row — status arrives as
// either a joined object ({name}) or a raw string, depending on the query.
function resolvePaymentStatusName(payment: Payment): string {
    const s = payment.status;
    if (s && typeof s === 'object') return (s as PaymentStatusObject).name ?? '';
    return typeof s === 'string' ? s : '';
}

const OrderOverviewTab: FC<OrderOverviewTabProps> = ({
                                                         currentOrder,
                                                         onOrderUpdate,
                                                         orderItems = [],
                                                         customerAddresses = null,
                                                         walletTransactions = [],
                                                         collectionPoint = null,
                                                         cakeOrder = null,
                                                         payments = [],
                                                     }) => {
    const { success, error } = useToast();
    const { stores, fetchStores } = useStores();
    const { updateOrderStatus } = useOrders();

    const [isCancelModalOpen,  setIsCancelModalOpen]  = useState(false);
    const [isSubmitting,       setIsSubmitting]       = useState(false);
    const [cakeExpanded,       setCakeExpanded]       = useState(false);
    const isChatOrder = currentOrder.type?.toLowerCase() === 'chat';
    const isCollectionOrder = currentOrder.delivery_type === 'collection';
    const status = currentOrder.status?.toUpperCase();
    const hideCancel = ['ORDER_CANCELLED', 'DELIVERY_COMPLETE', 'COLLECTION_COMPLETE', 'DELIVERY_FAILED', 'ON_ROUTE'].includes(status);

    useEffect(() => {
        if (stores.length === 0) void fetchStores();
    }, [fetchStores, stores.length]);

    const storeName = useMemo(() => {
        if (!currentOrder?.store_id) return 'No Store Linked';
        if (stores.length === 0) return 'Loading…';
        return stores.find(s => String(s.id) === String(currentOrder.store_id))?.name ?? 'Unknown Store';
    }, [currentOrder.store_id, stores]);

    const handleCancelConfirm = async () => {
        setIsSubmitting(true);
        try {
            await updateOrderStatus(String(currentOrder.id), 'ORDER_CANCELLED');
            await onOrderUpdate();
            success('Order cancelled');
            setIsCancelModalOpen(false);
        } catch (err) {
            error(err instanceof Error ? err.message : 'Failed to cancel order.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const fmt = (cents: number | string) => `R ${(Number(cents) / 100).toFixed(2)}`;

    const walletMath = walletTransactions.reduce((acc, tx) => {
        const amount = Math.abs(Number(tx.amount_cents || 0) / 100);
        if (tx.direction === 'debit') acc.paidFromWallet   += amount;
        else if (tx.direction === 'credit') acc.refundedToWallet += amount;
        return acc;
    }, { paidFromWallet: 0, refundedToWallet: 0 });

    // currentSubtotal is computed from orderItems, which already reflects
    // actual_price_cents (the real scanned price for deli/variable-weight
    // lines) via fetchOrderItemsFromDb — so it's the correct source of
    // truth for the order total, NOT currentOrder.total, which is a
    // stored column set once at order creation and never recalculated
    // when a line's price changes after picking. total_price is also
    // capped to the picked quantity when a line came up short at picking
    // (PARTIAL/MISSING), so a short pick lowers this total too.
    const currentSubtotal = orderItems.reduce((sum, i) => sum + Number(i.total_price || 0), 0) / 100;
    const deliveryFee = Number(currentOrder.delivery_fee || 0) / 100;
    const originalTotal = currentSubtotal + deliveryFee;
    const finalCharge = originalTotal - walletMath.paidFromWallet;
    const address = customerAddresses?.[0] ?? null;

    // True when any line was deli/scale-scanned AND its actual price
    // differs from the original catalog estimate — surfaced so anyone
    // looking at the order total understands why it differs from what
    // the customer originally saw at checkout.
    const hasDeliPriceChange = orderItems.some(
        (i) => i.actual_price_cents != null && i.actual_price_cents !== i.estimated_unit_price,
    );

    // True when any line came up short at picking (out of stock, damaged,
    // etc) — surfaced so the Final Charge makes sense at a glance when it's
    // lower than what the customer would expect from their ordered items.
    const hasPickingShortfall = orderItems.some(
        (i) => i.picking_status === 'PARTIAL' || i.picking_status === 'MISSING',
    );

    // Sum of card refunds across this order's payments. There's no dedicated
    // refunded-amount column in the schema (see PaymentsTab) — the only
    // signal is a payment's status containing "REFUND", in which case
    // captured_amount_cents is treated as the amount that went back. This is
    // separate from walletMath.refundedToWallet above, which tracks store
    // credit issued via wallet_transactions, not card refunds.
    const refundedToCard = payments
        .filter((p) => resolvePaymentStatusName(p).toUpperCase().includes('REFUND'))
        .reduce((sum, p) => sum + Number(p.captured_amount_cents ?? p.amount ?? 0), 0) / 100;

    return (
        <div className="space-y-5">

            {/* ── Header: ref + badges + cancel ───────────────────────── */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-xl font-bold text-stone-900 font-mono">{currentOrder.reference}</h2>
                        <StatusBadge status={status} />
                        <OrderTypeBadge type={currentOrder.type} />
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${
                            isCollectionOrder
                                ? 'bg-teal-50 text-teal-700 ring-teal-200'
                                : 'bg-sky-50 text-sky-700 ring-sky-200'
                        }`}>
                            {isCollectionOrder
                                ? <ShoppingBagIcon className="h-3 w-3" />
                                : <TruckIcon className="h-3 w-3" />}
                            {isCollectionOrder ? 'Collection' : 'Delivery'}
                        </span>
                        {cakeOrder && (
                            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset bg-pink-50 text-[#ee2b74] ring-[#ee2b74]/30">
                                <CakeIcon className="h-3 w-3" />
                                Includes cake
                            </span>
                        )}
                        {currentOrder.shared_basket_id && (
                            <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset bg-violet-50 text-violet-700 ring-violet-200">
                                <UsersIcon className="h-3 w-3" />
                                Shared basket
                            </span>
                        )}
                    </div>
                    <p className="text-[11px] text-stone-400 font-mono">{currentOrder.id}</p>
                </div>
                {!hideCancel && (
                    <button onClick={() => setIsCancelModalOpen(true)}
                            className="self-start inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 transition whitespace-nowrap">
                        Cancel Order
                    </button>
                )}
            </div>

            {/* ── Financial summary cards (ecom orders) ────────────────── */}
            {!isChatOrder && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-xl border border-stone-200 bg-white p-4">
                        <p className="text-xs text-stone-400 uppercase font-medium tracking-wide">Order Total</p>
                        <p className="mt-1.5 text-xl font-bold text-stone-900">R {originalTotal.toFixed(2)}</p>
                        {hasDeliPriceChange && (
                            <p className="mt-1 text-[11px] text-[#ee2b74] font-medium">
                                Updated from scanned weight/price
                            </p>
                        )}
                    </div>
                    <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
                        <p className="text-xs text-stone-400 uppercase font-medium tracking-wide">Wallet Applied</p>
                        <p className="mt-1.5 text-xl font-semibold text-stone-500">
                            {walletMath.paidFromWallet > 0 ? `- R ${walletMath.paidFromWallet.toFixed(2)}` : 'R 0.00'}
                        </p>
                    </div>
                    <div className={`rounded-xl border p-4 ${walletMath.refundedToWallet > 0 ? 'bg-[#f0f7e6] border-[#c5e08a]' : 'bg-white border-stone-200'}`}>
                        <p className={`text-xs uppercase font-medium tracking-wide ${walletMath.refundedToWallet > 0 ? 'text-[#5a7f28]' : 'text-stone-400'}`}>
                            Store Credits
                        </p>
                        <p className={`mt-1.5 text-xl font-bold ${walletMath.refundedToWallet > 0 ? 'text-[#5a7f28]' : 'text-stone-300'}`}>
                            R {walletMath.refundedToWallet.toFixed(2)}
                        </p>
                    </div>
                    <div className={`rounded-xl border p-4 ${refundedToCard > 0 ? 'bg-[#f0f7e6] border-[#c5e08a]' : 'border-[#ee2b74]/20 bg-pink-50/40'}`}>
                        <p className={`text-xs uppercase font-bold tracking-wide ${refundedToCard > 0 ? 'text-[#5a7f28]' : 'text-[#ee2b74]'}`}>Final Charge</p>
                        <p className="mt-1.5 text-xl font-bold text-stone-900">R {finalCharge.toFixed(2)}</p>
                        {hasDeliPriceChange && (
                            <p className="mt-1 text-[11px] text-[#ee2b74] font-medium">
                                Reflects actual weight/price
                            </p>
                        )}
                        {hasPickingShortfall && (
                            <p className="mt-1 flex items-center gap-1 text-[11px] text-amber-700 font-medium">
                                <ExclamationTriangleIcon className="h-3 w-3 shrink-0" />
                                Some items picked short
                            </p>
                        )}
                        {refundedToCard > 0 && (
                            <p className="mt-1 flex items-center gap-1 text-[11px] text-[#5a7f28] font-medium">
                                <ArrowUturnLeftIcon className="h-3 w-3 shrink-0" />
                                R {refundedToCard.toFixed(2)} refunded to card
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* ── Chat order summary cards ──────────────────────────────── */}
            {isChatOrder && (
                <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-stone-200 bg-white p-4">
                        <p className="text-xs text-stone-400 uppercase font-medium tracking-wide">Parcels</p>
                        <p className="mt-1.5 text-xl font-bold text-stone-900">{currentOrder.parcel_count ?? 0}</p>
                    </div>
                    <div className="rounded-xl border border-stone-200 bg-white p-4">
                        <p className="text-xs text-stone-400 uppercase font-medium tracking-wide">Created</p>
                        <p className="mt-1.5 text-sm font-semibold text-stone-900">
                            {new Date(currentOrder.created_at).toLocaleDateString('en-ZA', {
                                day: 'numeric', month: 'short', year: 'numeric',
                            })}
                        </p>
                    </div>
                </div>
            )}

            {/* ── Customer + Delivery/Collection ────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

                {/* Customer */}
                <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3 bg-stone-50 border-b border-stone-200">
                        <UserIcon className="h-3.5 w-3.5 text-stone-400" />
                        <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-widest">Customer</h3>
                    </div>
                    <div className="p-5">
                        {currentOrder.customer ? (
                            <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-full bg-[#ee2b74]/10 flex items-center justify-center flex-shrink-0">
                                    <span className="text-sm font-bold text-[#ee2b74]">
                                        {(currentOrder.customer.first_name?.[0] ?? '').toUpperCase()}
                                        {(currentOrder.customer.last_name?.[0] ?? '').toUpperCase()}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-stone-900">
                                        {currentOrder.customer.first_name} {currentOrder.customer.last_name}
                                    </p>
                                    {currentOrder.customer.cellphone && (
                                        <div className="flex items-center gap-1 mt-0.5">
                                            <PhoneIcon className="h-3 w-3 text-stone-400" />
                                            <p className="text-xs text-stone-500">{currentOrder.customer.cellphone}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                                <p className="text-xs text-amber-700">Customer details unavailable</p>
                                <p className="text-xs font-mono text-amber-600 mt-0.5">{String(currentOrder.customer_id)}</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Delivery / Collection */}
                <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3 bg-stone-50 border-b border-stone-200">
                        {isCollectionOrder
                            ? <ShoppingBagIcon className="h-3.5 w-3.5 text-stone-400" />
                            : <TruckIcon className="h-3.5 w-3.5 text-stone-400" />}
                        <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-widest">
                            {isCollectionOrder ? 'Collection Point' : 'Delivery Address'}
                        </h3>
                    </div>
                    <div className="p-5">
                        {isCollectionOrder ? (
                            collectionPoint ? (
                                <div className="space-y-1">
                                    <p className="text-sm font-semibold text-stone-900">{collectionPoint.name}</p>
                                    <div className="flex items-start gap-1.5">
                                        <MapPinIcon className="h-3.5 w-3.5 text-stone-400 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs text-stone-600">{collectionPoint.address_line1}</p>
                                            <p className="text-xs text-stone-500">
                                                {[collectionPoint.suburb, collectionPoint.city].filter(Boolean).join(', ')}
                                            </p>
                                        </div>
                                    </div>
                                    {collectionPoint.instructions && (
                                        <div className="mt-2 rounded-lg bg-teal-50 border border-teal-200 px-3 py-2">
                                            <p className="text-xs text-teal-700 italic">{collectionPoint.instructions}</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <p className="text-sm text-stone-400 italic">No collection point selected.</p>
                            )
                        ) : (
                            address ? (
                                <div className="flex items-start gap-1.5">
                                    <MapPinIcon className="h-3.5 w-3.5 text-stone-400 mt-0.5 flex-shrink-0" />
                                    <div className="space-y-0.5">
                                        <p className="text-sm font-semibold text-stone-900">{address.address_1}</p>
                                        {address.address_2 && <p className="text-xs text-stone-600">{address.address_2}</p>}
                                        <p className="text-xs text-stone-500">
                                            {[address.suburb, address.city, address.postcode].filter(Boolean).join(', ')}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-stone-400 italic">
                                    {isChatOrder ? 'No address linked to this customer.' : 'No delivery address on this order.'}
                                </p>
                            )
                        )}
                    </div>
                </div>
            </div>

            {/* ── Order meta ───────────────────────────────────────────── */}
            <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 bg-stone-50 border-b border-stone-200">
                    <BuildingStorefrontIcon className="h-3.5 w-3.5 text-stone-400" />
                    <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-widest">Order Details</h3>
                </div>
                <div className="p-5">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-4">
                        <div>
                            <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Store</p>
                            <p className="text-sm font-semibold text-stone-900">{storeName}</p>
                        </div>
                        <div>
                            <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Order Date</p>
                            <div className="flex items-center gap-1.5">
                                <CalendarIcon className="h-3.5 w-3.5 text-stone-400" />
                                <p className="text-sm text-stone-900">
                                    {new Date(currentOrder.created_at).toLocaleDateString('en-ZA', {
                                        day: 'numeric', month: 'short', year: 'numeric',
                                    })}
                                </p>
                            </div>
                        </div>
                        <div>
                            <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Order Type</p>
                            <p className="text-sm font-semibold text-stone-900 capitalize">{currentOrder.type}</p>
                        </div>
                        <div>
                            <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Fulfilment</p>
                            <p className="text-sm font-semibold text-stone-900">
                                {isCollectionOrder ? 'Collection' : 'Delivery'}
                            </p>
                        </div>
                        <div>
                            <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Status</p>
                            <StatusBadge status={status} />
                        </div>
                        {currentOrder.parcel_count !== undefined && currentOrder.parcel_count !== null && (
                            <div>
                                <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Parcels</p>
                                <p className="text-sm font-semibold text-stone-900">{currentOrder.parcel_count}</p>
                            </div>
                        )}
                        {!isChatOrder && (
                            <div>
                                <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Items</p>
                                <p className="text-sm font-semibold text-stone-900">{orderItems.length}</p>
                            </div>
                        )}
                        {currentOrder.delivery_slot_start && (
                            <div>
                                <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">
                                    {isCollectionOrder ? 'Collection Date' : 'Delivery Date'}
                                </p>
                                <div className="flex items-center gap-1.5">
                                    <CalendarIcon className="h-3.5 w-3.5 text-stone-400" />
                                    <p className="text-sm text-stone-900">
                                        {new Date(currentOrder.delivery_slot_start).toLocaleDateString('en-ZA', {
                                            day: 'numeric', month: 'short', year: 'numeric',
                                        })}
                                    </p>
                                </div>
                            </div>
                        )}
                        {currentOrder.delivery_slot_display_sast && (
                            <div>
                                <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Time Window</p>
                                <div className="flex items-center gap-1.5">
                                    <ClockIcon className="h-3.5 w-3.5 text-[#ee2b74]" />
                                    <p className="text-sm font-medium text-[#ee2b74]">{currentOrder.delivery_slot_display_sast}</p>
                                </div>
                            </div>
                        )}
                        {currentOrder.delivery_fee > 0 && (
                            <div>
                                <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Delivery Fee</p>
                                <p className="text-sm text-stone-900">{fmt(currentOrder.delivery_fee)}</p>
                            </div>
                        )}
                        {isCollectionOrder && (
                            <div>
                                <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Collection Confirmation</p>
                                <p className="text-sm font-semibold text-stone-900">Entered pin on collection</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Cake order card ───────────────────────────────────────── */}
            {cakeOrder && (
                <div className="rounded-xl border border-[#ee2b74]/25 bg-white overflow-hidden">
                    <button
                        type="button"
                        onClick={() => setCakeExpanded(p => !p)}
                        className="w-full flex items-center gap-2 px-5 py-3 bg-pink-50/60 border-b border-[#ee2b74]/20 hover:bg-pink-50 transition-colors text-left"
                    >
                        <CakeIcon className="h-3.5 w-3.5 text-[#ee2b74]" />
                        <h3 className="text-xs font-semibold text-[#ee2b74] uppercase tracking-widest">Cake Order</h3>
                        {cakeOrder.collected_at ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-[#f0f7e6] border border-[#c5e08a] px-2.5 py-0.5 text-xs font-semibold text-[#5a7f28]">
                                <CheckCircleIcon className="h-3 w-3" />
                                Collected
                            </span>
                        ) : (
                            <span className="rounded-full bg-amber-50 border border-amber-200 px-2.5 py-0.5 text-xs font-semibold text-amber-700">
                                Pending Collection
                            </span>
                        )}
                        <svg
                            className={`ml-auto h-4 w-4 text-[#ee2b74] transition-transform ${cakeExpanded ? 'rotate-180' : ''}`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    {cakeExpanded && (
                        <div className="p-5 space-y-4">
                            {/* Collection date + size */}
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                <div>
                                    <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Collection Date</p>
                                    <div className="flex items-center gap-1.5">
                                        <CalendarIcon className="h-3.5 w-3.5 text-[#ee2b74]" />
                                        <p className="text-sm font-semibold text-stone-900">
                                            {new Date(cakeOrder.collection_date).toLocaleDateString('en-ZA', {
                                                day: 'numeric', month: 'short', year: 'numeric',
                                            })}
                                        </p>
                                    </div>
                                </div>
                                {cakeOrder.selection?.size_label && (
                                    <div>
                                        <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Size</p>
                                        <p className="text-sm font-semibold text-stone-900">{String(cakeOrder.selection.size_label)}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Cake Total</p>
                                    <p className="text-sm font-semibold text-[#ee2b74]">{fmt(cakeOrder.total_price)}</p>
                                </div>
                            </div>

                            {/* Cake message */}
                            {cakeOrder.selection?.cake_message && (
                                <div className="rounded-lg bg-stone-50 border border-stone-200 px-4 py-3">
                                    <p className="text-[11px] text-stone-400 uppercase font-medium mb-1">Cake Message</p>
                                    <p className="text-sm text-stone-700 italic">"{String(cakeOrder.selection.cake_message)}"</p>
                                </div>
                            )}

                            {/* Line items */}
                            {cakeOrder.line_items?.length > 0 && (
                                <div>
                                    <p className="text-[11px] text-stone-400 uppercase font-medium mb-2">Cake Options</p>
                                    <div className="rounded-lg border border-stone-200 overflow-hidden">
                                        <table className="w-full text-sm">
                                            <tbody className="divide-y divide-stone-100">
                                            {cakeOrder.line_items.map((item, i) => (
                                                <tr key={i} className="bg-white">
                                                    <td className="px-4 py-2.5 text-stone-700">{item.label}</td>
                                                    <td className="px-4 py-2.5 text-right text-stone-500 tabular-nums">
                                                        {item.price > 0 ? fmt(item.price) : <span className="text-stone-300">Included</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Special notes */}
                            {cakeOrder.special_notes && (
                                <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
                                    <p className="text-[11px] text-amber-600 uppercase font-medium mb-1">Special Notes</p>
                                    <p className="text-sm text-amber-800">{cakeOrder.special_notes}</p>
                                </div>
                            )}

                            {/* Collection status */}
                            {cakeOrder.collected_at ? (
                                <div className="rounded-lg bg-[#f0f7e6] border border-[#c5e08a] px-4 py-3 flex items-start gap-2">
                                    <CheckCircleIcon className="h-4 w-4 text-[#5a7f28] mt-0.5 flex-shrink-0" />
                                    <div>
                                        <p className="text-xs font-semibold text-[#5a7f28]">Cake collected</p>
                                        <p className="text-xs text-[#5a7f28]/80 mt-0.5">
                                            {new Date(cakeOrder.collected_at).toLocaleString('en-ZA', {
                                                day: 'numeric', month: 'short', year: 'numeric',
                                                hour: '2-digit', minute: '2-digit',
                                            })}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-lg bg-sky-50 border border-sky-200 px-4 py-3 flex items-start gap-2">
                                    <ShoppingBagIcon className="h-4 w-4 text-sky-500 mt-0.5 flex-shrink-0" />
                                    <p className="text-xs text-sky-700">
                                        The cake must be collected in-store on the date above. It cannot be included in a delivery even if the rest of this order is being delivered.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Line items + totals ──────────────────────────────────── */}
            {orderItems.length > 0 && (
                <div className="rounded-xl border border-stone-200 bg-white overflow-hidden">
                    <div className="flex items-center gap-2 px-5 py-3 bg-stone-50 border-b border-stone-200">
                        <ShoppingBagIcon className="h-3.5 w-3.5 text-stone-400" />
                        <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-widest">Line Items</h3>
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
                            <tr>
                                <th className="text-left py-2.5 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Product</th>
                                <th className="text-right py-2.5 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Qty</th>
                                <th className="text-right py-2.5 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Unit</th>
                                <th className="text-right py-2.5 px-5 text-xs font-semibold text-stone-500 uppercase tracking-wide">Total</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                            {orderItems.map(item => {
                                const pickingShortfall = buildPickingShortfall(item);
                                return (
                                    <tr key={item.id} className="hover:bg-stone-50 transition">
                                        <td className="py-3 px-5 text-stone-900 font-medium">{item.product_name}</td>
                                        <td className="text-right py-3 px-5 text-stone-600">
                                            <div className="flex flex-col items-end gap-1">
                                                <span>{item.quantity}</span>
                                                {pickingShortfall && (
                                                    <span className={`inline-flex items-center gap-1 rounded-full pl-1.5 pr-2 py-0.5 text-[10px] font-semibold whitespace-nowrap ${
                                                        pickingShortfall.tone === 'red'
                                                            ? 'bg-red-50 text-red-700'
                                                            : 'bg-amber-50 text-amber-700'
                                                    }`}>
                                                        <ExclamationTriangleIcon className="h-3 w-3 shrink-0" />
                                                        {pickingShortfall.label}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-right py-3 px-5 text-stone-500">{fmt(item.unit_price)}</td>
                                        <td className="text-right py-3 px-5 font-semibold text-stone-900">{fmt(item.total_price)}</td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                    <div className="border-t border-stone-200 bg-stone-50 px-5 py-4 space-y-1.5">
                        <div className="flex justify-end gap-6 text-sm text-stone-500">
                            <span className="flex items-center gap-1.5">
                                Subtotal
                                {hasDeliPriceChange && (
                                    <span className="inline-flex items-center rounded-full bg-pink-50 px-2 py-0.5 text-[10px] font-semibold text-[#ee2b74]">
                                        actual
                                    </span>
                                )}
                            </span>
                            <span className="w-28 text-right font-medium text-stone-700">R {currentSubtotal.toFixed(2)}</span>
                        </div>
                        {deliveryFee > 0 && (
                            <div className="flex justify-end gap-6 text-sm text-stone-500">
                                <span>Delivery Fee</span>
                                <span className="w-28 text-right">R {deliveryFee.toFixed(2)}</span>
                            </div>
                        )}
                        {walletMath.paidFromWallet > 0 && (
                            <div className="flex justify-end gap-6 text-sm text-stone-500">
                                <span>Wallet Applied</span>
                                <span className="w-28 text-right text-stone-600">- R {walletMath.paidFromWallet.toFixed(2)}</span>
                            </div>
                        )}
                        {walletMath.refundedToWallet > 0 && (
                            <div className="flex justify-end">
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f0f7e6] pl-2.5 pr-3 py-1 text-xs font-semibold text-[#5a7f28]">
                                    <BanknotesIcon className="h-3.5 w-3.5 shrink-0" />
                                    Store credit issued
                                    <span className="ml-1">R {walletMath.refundedToWallet.toFixed(2)}</span>
                                </span>
                            </div>
                        )}
                        <div className="flex justify-end gap-6 border-t border-stone-200 pt-2 mt-1">
                            <span className="text-sm font-bold text-stone-900">Final Charge</span>
                            <span className="text-lg font-black text-[#ee2b74] w-28 text-right">R {finalCharge.toFixed(2)}</span>
                        </div>
                    </div>
                </div>
            )}

            <CancelOrderModal
                isOpen={isCancelModalOpen}
                orderRef={currentOrder.reference}
                onClose={() => setIsCancelModalOpen(false)}
                onConfirm={handleCancelConfirm}
                isSubmitting={isSubmitting}
            />
        </div>
    );
};

export default OrderOverviewTab;